import { useEffect, useState } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { RoomModel } from '../../types/protocols';
import { componentKeysInDateIndex } from '../../Pages/Archive/archiveDateIndex';

/**
 * A component snapshot from `archive/changeLog`: the component node verbatim
 * plus metadata. Fields are read with the archivedField* helpers, since a
 * cleared ValueWithMetadata is stored as just `{status}`.
 */
export interface ArchivedComponent {
    type: string;
    specificType?: unknown;
    label?: unknown;
    comment?: unknown;
    condition?: unknown;
    _archiveMetadata: {
        archivedAt: string;
        archivedDate: string;
        buildingKey: string;
        componentKey: string;
        deleted?: boolean;
    };
    /** `{changes: [{field, oldValue, newValue, changedBy}]}` against the previous archived day */
    _diff?: unknown;
}

export interface BuildingChanges {
    buildingKey: string;
    buildingName: string;
    components: ArchivedComponent[];
    /** The building's rooms now, to name rooms in room changes */
    rooms: { [key: string]: RoomModel };
}

export interface DateActivity {
    date: string;
    componentCount: number;
    buildingCount: number;
}

/** Every `YYYY-MM-DD` from start to end, inclusive. */
export const getDateRange = (start: string, end: string): string[] => {
    const dates: string[] = [];
    const last = new Date(`${end}T00:00:00Z`);
    for (let d = new Date(`${start}T00:00:00Z`); d <= last; d.setUTCDate(d.getUTCDate() + 1)) {
        dates.push(d.toISOString().split('T')[0]);
    }
    return dates;
};

/** Number of changed components (and buildings) per day in the period. */
export const useArchiveActivity = (startDate: string, endDate: string) => {
    const [activities, setActivities] = useState<DateActivity[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        const db = getDatabase();

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const results = await Promise.all(getDateRange(startDate, endDate).map(async (date) => {
                    const snapshot = await get(ref(db, `archive/changeLog/byDate/${date}`));
                    const data = (snapshot.val() ?? {}) as Record<string, unknown>;
                    const counts = Object.values(data)
                        .map(entry => componentKeysInDateIndex(entry).length)
                        .filter(count => count > 0);
                    return {
                        date,
                        componentCount: counts.reduce((sum, count) => sum + count, 0),
                        buildingCount: counts.length,
                    };
                }));
                if (!cancelled) setActivities(results);
            } catch (err) {
                console.error('Error loading archive activity:', err);
                if (!cancelled) setError('Kunde inte ladda ändringsloggen.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    return { activities, loading, error };
};

/** The archived component snapshots of one day, grouped by building. */
export const useArchiveDayChanges = (date: string | null) => {
    const [changes, setChanges] = useState<BuildingChanges[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setChanges([]);
        setError(null);
        if (!date) return;

        let cancelled = false;
        const db = getDatabase();

        const load = async () => {
            setLoading(true);
            try {
                const indexSnapshot = await get(ref(db, `archive/changeLog/byDate/${date}`));
                const indexData = (indexSnapshot.val() ?? {}) as Record<string, unknown>;

                const buildings = await Promise.all(Object.entries(indexData).map(async ([buildingKey, entry]) => {
                    const componentKeys = componentKeysInDateIndex(entry);
                    if (componentKeys.length === 0) return null;

                    const [nameSnapshot, roomsSnapshot, ...componentSnapshots] = await Promise.all([
                        get(ref(db, `buildingsSummary/${buildingKey}/name`)),
                        get(ref(db, `buildings/${buildingKey}/structure/rooms`)),
                        ...componentKeys.map(componentKey =>
                            get(ref(db, `archive/changeLog/${buildingKey}/components/${componentKey}/${date}`))
                        ),
                    ]);
                    const components = componentSnapshots
                        .filter(snapshot => snapshot.exists())
                        .map(snapshot => snapshot.val() as ArchivedComponent);
                    if (components.length === 0) return null;

                    const name = nameSnapshot.val();
                    return {
                        buildingKey,
                        buildingName: typeof name === 'string' ? name : buildingKey,
                        components,
                        rooms: (roomsSnapshot.val() ?? {}) as { [key: string]: RoomModel },
                    };
                }));

                if (!cancelled) {
                    setChanges(buildings
                        .filter((b): b is BuildingChanges => b !== null)
                        .sort((a, b) => a.buildingName.localeCompare(b.buildingName, 'sv')));
                }
            } catch (err) {
                console.error('Error loading archive day:', err);
                if (!cancelled) setError('Kunde inte ladda ändringarna för dagen.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [date]);

    return { changes, loading, error };
};
